import React, { Suspense, useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  AUDIO_VIEWER,
  CLEVERSTORY_VIEWER,
  ENHANCED_VIEWER,
  HTML_APP_VIEWER,
  HTML_VIEWER,
  IMAGE_VIEWER,
  PDF_VIEWER,
  RENDER_IN_VIEWER,
  RENDER_OPTIMIZED_VIEW,
  SCORM_VIEWER,
  SPREADSHEET_VIEWER,
  VIDEO_VIEWER,
} from "src/constants/assets";
import EnhancedDocumentViewer from "./documentViewer";
import DefaultViewer from "./defaultViewer";
import AuthService from "src/utils/auth.service";
import {
  fetchPreviewHtmlTemplate,
  recordAnalyticsEvent,
  invalidateAsset,
  recordSessionAnalyticsEvent,
} from "src/redux/actions/assets";
import { triggerAssetProcessing } from "src/redux/actions/assets";
import { FileService } from "src/utils/file.service";
import { canRecordAssetView } from "src/utils/utils";
import ExcelViewer from "./excelViewer";

import _ from "lodash";
import cx from "classnames";
import styles from "./assetViewer.module.scss";

import "@paperflite-marketing/asset-viewer/dist/index.css";
import "videojs-seek-buttons/dist/videojs-seek-buttons.css";
import "videojs-seek-buttons";
import "video.js/dist/video-js.css";
import { useLocation } from "react-router-dom";
import VideoContainer from "./videoViewer";
import AssetService from "src/utils/asset.service";

const NotificationImage = "/images/assets/notification_empty_message.svg";
const ErrorImage = "/images/assets/404error_message.svg";
const AlbumCover = "/images/albumcover.jpg";
const defaultUrlIcon = "/images/invalidFileType/url.jpg";

const HTMLViewer = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.HTMLViewer,
  })),
);

const ScormViewer = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.ScormViewer,
  })),
);

const ImageViewer = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.ImageViewer,
  })),
);

const VideoViewer = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.VideoViewer,
  })),
);

const AudioViewer = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => {
    return {
      default: module.AudioViewer,
    };
  }),
);

const AssetViewer = ({ isSidePanelOpen, isLeftPanelOpen, showTableOfContents, setShowTableOfContents, showPPTxNotes, setShowPPTxNotes, loading}) => {
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  const location = useLocation();

  const restrictedFileTypes = ["IMAGE", "VIDEO", "AUDIO", "PDF"];
  const STATUS_OK = "ok";

  const asset = useSelector((state) => _.get(state, "asset.item", null));
  const type = useSelector((state) => _.get(state, "asset.item.viewer.type", ""));
  const subtitleEnabled = useSelector((state) =>
    _.get(state, "account.item.subtitleEnabled", true),
  );

  const session = useSelector((state) => _.get(state, "asset.session", null));
  const [fileType, setFileType] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date().getTime());

  const processing = useSelector((state) => {
    const assetProcessing = _.get(state, "asset.process.processing", false);

    const assetMergeStatus = _.get(state, "asset.merge", {});

    const selectedAssetId = _.get(state, "asset.selectedAssetId", null);

    if (assetProcessing) {
      return true;
    }

    if (
      _.size(assetMergeStatus) > 0 &&
      _.get(assetMergeStatus, "status") !== "ok"
    ) {
      return true;
    }

    if (_.size(selectedAssetId) > 0 && !assetProcessing) {
      return true;
    }

    return false;
  });

  const isAssetPreparing = useSelector((state) =>
    _.get(state, "asset.preparing", false),
  );

  const processingCreatedDate = _.get(asset,"processingTransaction.createdDate");
  const status = _.get(asset, "status");

  const preview = useSelector((state) => _.get(state, "asset.preview", null));

  const [width, setWidth] = useState(1024);

  const [assetProcessingCreatedDate, setAssetProcessingCreatedDate] = useState(
    processingCreatedDate,
  );

  const [reprocessingStartTime, setReprocessingStartTime] = useState(null);

  const sessionRef = useRef(session);

  const recordNewSession = (assetId) => {
    var viewDate = new Date().getTime();

    dispatch(
      recordSessionAnalyticsEvent({
        type: 100,
        eventDate: viewDate,
        asset: assetId,
      }),
    );
  };

  const handleWidowResize = () => {
    calculatePreferredWidth();
  };

  const startInterval = () => {
    intervalRef.current = setInterval(() => {
      setCurrentTime(+new Date());
    }, 3000);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleReProcessAsset = () => {
    if (!processing && status !== STATUS_OK) {
      const now = new Date().getTime();
      setReprocessingStartTime(now);
      setCurrentTime(now);
      dispatch(triggerAssetProcessing(asset.id));
      startInterval();
    }
  };

  const renderProcessing = () => {
    const twoMinutes = 2 * 60 * 1000;
    const fiveMinutes = 5 * 60 * 1000;

    const isNewUpload = !processing;
    const modifiedDate = asset.modifiedDate;
    let effectiveStart;

    if (reprocessingStartTime) {
      effectiveStart = reprocessingStartTime;
    } else if (isNewUpload) {
      effectiveStart =
        assetProcessingCreatedDate < modifiedDate
          ? modifiedDate
          : processingCreatedDate;
    } else {
      effectiveStart = assetProcessingCreatedDate || modifiedDate;
    }

    const timeElapsed = Math.abs(currentTime - effectiveStart);

    if (timeElapsed >= 0 && timeElapsed < twoMinutes) {
      return (
        <ProcessingMessage
          content="Hang tight! Your file is processing..."
          icon={NotificationImage}
        />
      );
    } else if (timeElapsed >= twoMinutes && timeElapsed < fiveMinutes) {
      return (
        <ProcessingMessage
          content="Just a bit longer... Your file is taking time to process."
          icon={NotificationImage}
        />
      );
    } else if (timeElapsed >= fiveMinutes) {
      stopInterval();
      return (
        <ProcessingMessage
          content="Uh-oh, something’s off. We couldn’t process your file."
          icon={ErrorImage}
          processingDelayed={true}
          onClick={() => handleReProcessAsset()}
        />
      );
    }

    return null;
  };

  const calculatePreferredWidth = () => {
    const viewerContainer = document.querySelector(`.${styles.assetViewer}`);
    if (viewerContainer) {
      const width = viewerContainer.offsetWidth;
      setWidth(width);
    }
  };

  const onAnalyticsChange = () => {
    var viewDate = new Date().getTime();
    if (!session.id) {
      dispatch(
        recordSessionAnalyticsEvent({
          type: 100,
          asset: asset.id,
          eventDate: viewDate,
        }),
      );
    }
  };

  useEffect(() => {

    window.addEventListener("resize", handleWidowResize);
    calculatePreferredWidth();

    return () => {
      window.removeEventListener("resize", handleWidowResize);
      dispatch(invalidateAsset());
    };
  }, []);

  useEffect(() => {
    if(!loading){
    // Record New Session for the asset
    if (canRecordAssetView(asset)) {
      recordNewSession(asset.id);
    }
    }
  }, [loading])

  useEffect(() => {

    return () => {
      if (session && session.id && !loading) {
        var viewDate = new Date().getTime();
        dispatch(recordAnalyticsEvent({
          type: 104,
          session: session.id,
          eventDate: viewDate,
          asset: asset.id
        }))
      }
    }

  }, [_.get(session, "id", null), loading]);

  // Keep sessionRef updated with the latest session value
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    calculatePreferredWidth();
  }, [isSidePanelOpen]);

  useEffect(() => {
    startInterval();
    return () => {
      stopInterval();
    };
  }, []);

  useEffect(() => {
    if (status !== STATUS_OK && processing) {
      startInterval();
    } else if (status === STATUS_OK) {
      stopInterval();
      setReprocessingStartTime(null); // Reset reprocessing time when successful
    }
  }, [status]);

  useEffect(() => {
    if (asset && asset.metadata) {
      const type = FileService.getFileType(asset.metadata);
      setFileType(type);
    }

    console.log("🚀 ~ useEffect ~ viewer type:", type);
    if (
      (type === HTML_VIEWER || type === CLEVERSTORY_VIEWER) &&
      _.get(asset, "viewer.url")
    ) {
      dispatch(fetchPreviewHtmlTemplate({ url: _.get(asset, "viewer.url") }));
    }
  }, [asset, type]);

  useEffect(() => {
    if (assetProcessingCreatedDate !== processingCreatedDate) {
      setAssetProcessingCreatedDate(processingCreatedDate);
    }
  }, [processingCreatedDate]);

  useEffect(() => {
    if (type === IMAGE_VIEWER) {
      // image transform calculation & animation
      const imageElement = document.getElementById(
        `image_view_asset_viewer_${asset.id}`,
      );
      if (imageElement) {
        const parent = imageElement.parentElement;
        const transformValue = window
          .getComputedStyle(parent)
          .getPropertyValue("transform");

        let scale = new DOMMatrix(transformValue).a;
        let translateX = new DOMMatrix(transformValue).e;

        if (isSidePanelOpen) {
          translateX = translateX - 175;
        } else {
          translateX = translateX + 175;
        }
        let translateY = new DOMMatrix(transformValue).f;

        parent.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
        parent.style.transition = "transform 0.5s ease";
      }
    }
  }, [isSidePanelOpen]);

  const renderViewerType = () => {
    const viewerElement = document.querySelector(`.${styles.assetViewer}`);
    const viewerWidth = viewerElement ? viewerElement.offsetWidth : 1024;

    switch (type) {
      case ENHANCED_VIEWER:
      case PDF_VIEWER: {
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get("page");

        return (
          <EnhancedDocumentViewer
            asset={asset}
            width={width}
            isSidePanelOpen={isSidePanelOpen}
            processingAsset={processing || isAssetPreparing}
            initialPage={page}
            showTableOfContents={showTableOfContents}
            setShowTableOfContents={setShowTableOfContents}
            showPPTxNotes={showPPTxNotes}
            setShowPPTxNotes={setShowPPTxNotes}
          />
        );
      }

      case IMAGE_VIEWER: {
        const getPreviewUrl = (asset) => {
          var url = AuthService.getURL(`api/2.0/assets/${asset.id}/download`, {
            modifiedDate: AssetService.getNumericValue(asset, "metadata.modified_date"),
            preview: true,
          });

          if (_.get(asset, "viewer.url")) {
            url = _.get(asset, "viewer.url");
          }

          return url;
        };

        const imageUrl = getPreviewUrl(asset);
        return (
          <Suspense fallback={<div className={styles.imageViewer}></div>}>
            <ImageViewer
              key={asset.id}
              isLeftPanelOpen={isLeftPanelOpen}
              isSidePanelOpen={isSidePanelOpen}
              width={viewerWidth}
              id={`asset_viewer_${asset.id}`}
              className={styles.imageViewer}
              mode="light"
              src={imageUrl}
              scrollParent={`.${styles.assetViewer}`}
              loadingComponent={() => {
                return (
                  <div className={cx(styles.loader)}>
                    <label className={styles.message}>Loading Image....</label>
                  </div>
                );
              }}
              active={true}
            />
          </Suspense>
        );
      }

      case VIDEO_VIEWER: {
        let videoUrl = AuthService.getURL(
          `api/2.0/assets/${asset.id}/download`,
          {
            modifiedDate: AssetService.getNumericValue(asset, "metadata.modified_date"),
            preview: true,
          },
        );
        const searchParams = new URLSearchParams(location.search);
        const segment = searchParams.get("segment");
        var iconUrl = _.get(asset, "icon.full");
        var poster = _.get(asset, "poster");

        return (
          <Suspense fallback={<div className={styles.videoViewer}></div>}>
            <VideoContainer
              key={asset.id}
              asset={asset}
              initialSegment={segment}
              pictureInPictureToggle={true}
              fullscreenToggle={true}
              recordAnalytics={true}
            />
          </Suspense>
        );
      }

      case AUDIO_VIEWER: {
        var sourceUrl = AuthService.getURL(
          `api/2.0/assets/${asset.id}/download`,
          {
            modifiedDate: AssetService.getNumericValue(asset, "metadata.modified_date"),
            preview: true,
          },
        );

        const iconUrl = _.get(asset, "icon.thumbnail", AlbumCover);
        const poster = _.get(asset, "poster");

        return (
          <Suspense fallback={<div className={styles.audioViewer}></div>}>
            <AudioViewer
              key={asset.id}
              id={`asset_viewer_${asset.id}`}
              className={cx(styles.audioViewer)}
              src={sourceUrl}
              metadata={asset.metadata}
              active={true}
              icon={iconUrl}
              poster={poster}
              onPlay={() => {
                // console.log("Audio started playing");
              }}
              onAnalyticsChange={!loading && onAnalyticsChange}
            />
          </Suspense>
        );
      }
      case HTML_VIEWER:
      case CLEVERSTORY_VIEWER: {
        const name = _.get(asset, "name");

        var url = _.get(asset, "metadata.url");
        var mode = _.get(asset, "viewer.mode");
        var optimizedUrl = null;

        if (type === CLEVERSTORY_VIEWER && _.get(asset, "viewer.mode")) {
          url = _.get(asset, "viewer.url");
          mode = _.get(asset, "viewer.mode");
        } else if (mode === RENDER_IN_VIEWER) {
          url = _.get(asset, "metadata.url");
        } else if (mode === RENDER_OPTIMIZED_VIEW) {
          optimizedUrl = _.get(preview, "htmlPreview");
        }
        const iconUrl = _.get(asset, "icon.key");
        const canPreview = _.get(asset, "viewer.preview");
        

        return (
          <Suspense fallback={<div className={styles.htmlViewer}></div>}>
            <HTMLViewer
              key={asset.id}
              id={`asset_viewer_${asset.id}`}
              className={styles.htmlViewer}
              name={name}
              icon={iconUrl}
              mode={mode}
              url={url}
              optimizedUrl={optimizedUrl}
              canPreview={canPreview}
              active={true}
              onAnalyticsChange={!loading && onAnalyticsChange}
            />
          </Suspense>
        );
      }
      case HTML_APP_VIEWER: {
        const id = _.get(asset, "id");
        const url = _.get(asset, "viewer.url");

        return (
          <Suspense fallback={<div className={styles.htmlViewer}></div>}>
            <ScormViewer
              key={id}
              id={id}
              className={styles.htmlViewer}
              url={url}
              active={true}
            />
          </Suspense>
        );
      }

      case SCORM_VIEWER: {
        const id = _.get(asset, "id");
        const url = _.get(asset, "viewer.url");

        return (
          <Suspense fallback={<div className={styles.htmlViewer}></div>}>
            <ScormViewer
              key={id}
              id={id}
              className={styles.htmlViewer}
              url={url}
              active={true}
            />
          </Suspense>
        );
      }

      case SPREADSHEET_VIEWER: {
        return <ExcelViewer key={asset.id} asset={asset} />;
      }

      default:
        return <DefaultViewer key={asset.id} asset={asset} />;
    }
  };

  return (
    <div
      className={cx(styles.assetViewer, {
        [styles.modalOpened]: isSidePanelOpen && type === ENHANCED_VIEWER,
        [styles.modalShiftRight]: isLeftPanelOpen && type !== IMAGE_VIEWER,
        [styles.modalShiftRightImageViewer]: isLeftPanelOpen && type === IMAGE_VIEWER,
      })}
    >
      {status !== STATUS_OK &&
        !restrictedFileTypes.includes(fileType) &&
        renderProcessing()}

      {(status === STATUS_OK || restrictedFileTypes.includes(fileType)) &&
        renderViewerType(session)}
    </div>
  );
};

const ProcessingMessage = ({ content, icon, processingDelayed, onClick }) => {
  return (
    <div className={styles.processingErrorMessage}>
      <div className={styles.innerContainer} />
      <img className={styles.processingImage} src={icon} alt="error" />
      <p className={styles.defaultMessage}>{content}</p>

      {processingDelayed && (
        <button onClick={onClick} className={styles.errorButton}>
          Try Again
        </button>
      )}
    </div>
  );
};

export default AssetViewer;
